import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/axios';
import Pagination from '../Pagination';
import DraftCard from './DraftCard';
import PageStrip from './PageStrip';
import PagePicker from './PagePicker';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import {
  createProducts, deleteImport, errorMessage, getImport, listItems, rereadPage, retryImport, selectPages,
} from '../../api/catalogImportApi';
import { IMPORT_STATUS, btnDanger, btnGhost, btnPrimary, card, muted } from './styles';

const POLL_MS = 4000;
const PAGE_SIZE = 10;
const TABS = [
  { key: 'PENDING_REVIEW', text: 'Needs review' },
  { key: 'CREATED', text: 'Created' },
  { key: 'LINKED', text: 'Used existing' },
  { key: 'DISCARDED', text: 'Discarded' },
  { key: '', text: 'All' },
];

// AWAITING_PAGES counts as active so the screen notices when reading auto-starts.
const isActive = (s) => s && (s.running || ['UPLOADED', 'AWAITING_PAGES', 'PROCESSING'].includes(s.status));

/** Review screen for one import: progress while Claude reads the pages, then the drafts. */
export default function CatalogImportDetail({ importId, onBack }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { addToast } = useToast();

  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('PENDING_REVIEW');
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [failures, setFailures] = useState([]);
  const [startingPages, setStartingPages] = useState(false);

  const summary = detail?.summary;
  const active = isActive(summary);
  const wasActive = useRef(false);

  const loadDetail = useCallback(async () => {
    try {
      const res = await getImport(importId);
      setDetail(res.data);
      setLoadError('');
      return res.data;
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load this import.'));
      return null;
    }
  }, [importId]);

  const loadPages = useCallback(async () => {
    const d = await loadDetail();
    if (!d) throw new Error('Could not load the catalog pages.');
    return d.pages;
  }, [loadDetail]);

  const loadItems = useCallback(() => {
    setItemsLoading(true);
    listItems(importId, tab, page, PAGE_SIZE)
      .then((r) => { setItems(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => { setItems([]); setTotalPages(0); })
      .finally(() => setItemsLoading(false));
  }, [importId, tab, page]);

  const loadBrands = useCallback(() => {
    api.get('/brands').then((r) => setBrands(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    loadBrands();
    api.get('/categories').then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [loadBrands]);
  useEffect(() => { setSelected(new Set()); }, [tab, page]);

  // Poll while pages are being read; reload drafts as they arrive and once more when done.
  useEffect(() => {
    if (!active) {
      if (wasActive.current) { wasActive.current = false; loadItems(); }
      return undefined;
    }
    wasActive.current = true;
    const t = setInterval(() => { loadDetail(); loadItems(); }, POLL_MS);
    return () => clearInterval(t);
  }, [active, loadDetail, loadItems]);

  const onItemChanged = useCallback((updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  }, []);

  const onDirtyChange = useCallback((id, dirty) => {
    setDirtyIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const pendingOnPage = items.filter((i) => i.status === 'PENDING_REVIEW');
  const allSelected = pendingOnPage.length > 0 && pendingOnPage.every((i) => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pendingOnPage.map((i) => i.id)));

  /**
   * Creates products from drafts. Brands that don't exist yet are never created silently: the
   * reviewer confirms them by name first (backend refuses unconfirmed new brands too).
   * Returns true when every draft was created.
   */
  const runCreate = async (drafts) => {
    const known = new Set(brands.map((b) => b.name.toLowerCase()));
    const newBrands = [...new Set(drafts.map((d) => (d.brandName || '').trim()).filter((b) => b && !known.has(b.toLowerCase())))];
    if (newBrands.length > 0) {
      const ok = await confirm({
        title: `Create ${newBrands.length > 1 ? `${newBrands.length} new brands` : 'a new brand'}?`,
        message: `${newBrands.join(', ')} ${newBrands.length > 1 ? "aren't" : "isn't"} in the catalogue yet. ${newBrands.length > 1 ? 'They' : 'It'} will be created before the products. Check the spelling — brand names show on the storefront.`,
        confirmLabel: 'Create brand & products',
      });
      if (!ok) return false;
    }

    setCreating(true);
    try {
      const res = await createProducts(importId, drafts.map((d) => d.id), newBrands);
      const results = res.data || [];
      const ok = results.filter((r) => r.created);
      const failed = results.filter((r) => !r.created);
      const names = Object.fromEntries(drafts.map((d) => [d.id, d.name || `Draft #${d.id}`]));
      setFailures(failed.map((f) => ({ ...f, name: names[f.itemId] })));
      if (ok.length > 0) {
        const pending = ok.filter((r) => r.approvalStatus === 'PENDING').length;
        addToast(`${ok.length} product${ok.length > 1 ? 's' : ''} created${summary?.campaign ? ' and added to the campaign' : ''}${pending ? ` — ${pending} waiting for admin approval` : ''}.`, 'success', 5000);
      }
      if (failed.length > 0) addToast(`${failed.length} draft${failed.length > 1 ? 's' : ''} couldn't be created — see the list above the drafts.`, 'error', 6000);
      setSelected(new Set());
      if (newBrands.length > 0) loadBrands();
      loadItems();
      loadDetail();
      return failed.length === 0;
    } catch (err) {
      addToast(errorMessage(err, 'Could not create products.'), 'error');
      return false;
    } finally {
      setCreating(false);
    }
  };

  const createSelected = () => {
    const drafts = items.filter((i) => selected.has(i.id));
    const unsaved = drafts.filter((d) => dirtyIds.has(d.id));
    if (unsaved.length > 0) {
      addToast(`Save your changes on ${unsaved.map((d) => d.name || `#${d.id}`).join(', ')} first.`, 'warning', 5000);
      return;
    }
    const undecided = drafts.filter((d) => d.duplicateDecisionNeeded);
    if (undecided.length > 0) {
      addToast(`${undecided.map((d) => d.name || `#${d.id}`).join(', ')} may already be in the catalogue — mark ${undecided.length > 1 ? 'them' : 'it'} as the same or a different product first.`, 'warning', 6000);
      return;
    }
    runCreate(drafts);
  };

  const handleRetry = async () => {
    try { await retryImport(importId); loadDetail(); }
    catch (err) { addToast(errorMessage(err, 'Could not retry.'), 'error'); }
  };

  const handleSelectPages = async (pageNumbers) => {
    setStartingPages(true);
    try { await selectPages(importId, pageNumbers); await loadDetail(); }
    catch (err) { addToast(errorMessage(err, 'Could not start reading the pages.'), 'error'); }
    finally { setStartingPages(false); }
  };

  const handleReread = async (pageNumber) => {
    const skipped = detail?.pages.find((p) => p.pageNumber === pageNumber)?.status === 'SKIPPED';
    const ok = await confirm(skipped ? {
      title: `Read page ${pageNumber}?`,
      message: 'This page was skipped. Reading it sends it to AI (about ₹2–3) and adds any products found as drafts.',
      confirmLabel: 'Read page',
    } : {
      title: `Re-read page ${pageNumber}?`,
      message: 'Drafts from this page that are still waiting for review will be replaced, including any edits to them.',
      confirmLabel: 'Re-read page', tone: 'danger',
    });
    if (!ok) return false;
    try { await rereadPage(importId, pageNumber); loadDetail(); return true; }
    catch (err) { addToast(errorMessage(err, 'Could not re-read the page.'), 'error'); return false; }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete this import?',
      message: 'The uploaded PDF and every draft will be removed. Products already created from it are not affected.',
      confirmLabel: 'Delete import', tone: 'danger',
    });
    if (!ok) return;
    try { await deleteImport(importId); onBack(); }
    catch (err) { addToast(errorMessage(err, 'Could not delete the import.'), 'error'); }
  };

  if (loadError && !detail) {
    return (
      <div style={{ padding: '24px 0' }}>
        <button style={btnGhost} onClick={onBack}>← All imports</button>
        <p role="alert" style={{ color: '#B91C1C', marginTop: 16 }}>{loadError}</p>
      </div>
    );
  }
  if (!detail) return <p style={{ color: '#9CA3AF', padding: '24px 0' }}>Loading…</p>;

  const status = IMPORT_STATUS[summary.status] || IMPORT_STATUS.UPLOADED;
  const pct = summary.pageCount ? Math.round((summary.pagesProcessed / summary.pageCount) * 100) : 0;
  const failedPages = detail.pages.filter((p) => p.status === 'FAILED').length;
  const counts = summary.itemCounts || {};

  return (
    <div style={{ padding: '24px 0' }}>
      <button style={{ ...btnGhost, marginBottom: 16 }} onClick={onBack}>← All imports</button>

      <section style={card} aria-label="Import progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 6px', wordBreak: 'break-word' }}>{summary.fileName}</h1>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={status.style}>{status.text}</span>
              <span style={muted}>
                {counts.PENDING_REVIEW || 0} to review · {counts.CREATED || 0} created · {counts.LINKED || 0} used existing · {counts.MERGED ? `${counts.MERGED} merged · ` : ''}{counts.DISCARDED || 0} discarded
                {summary.aiCostUsd > 0 && <> · AI cost ≈ ${summary.aiCostUsd.toFixed(2)}</>}
              </span>
            </div>
            {summary.campaign && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#374151' }}>
                🏪 Reviewed products go into Brandstore campaign <strong>{summary.campaign.name}</strong>
                {summary.campaign.companyName && <> for <strong>{summary.campaign.companyName}</strong></>}.
                {user?.role !== 'ADMIN' && (
                  <span style={{ display: 'block', fontSize: 12, color: '#92400E', marginTop: 2 }}>
                    Products you create start as Pending — they appear in the campaign once an admin approves them.
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(failedPages > 0 || summary.status === 'FAILED' || (summary.status === 'PROCESSING' && !summary.running)) && (
              <button style={btnGhost} disabled={summary.running} onClick={handleRetry}>
                {failedPages > 0 ? `Retry ${failedPages} failed page${failedPages > 1 ? 's' : ''}` : 'Resume'}
              </button>
            )}
            <button style={btnDanger} disabled={summary.running} onClick={handleDelete}>Delete import</button>
          </div>
        </div>

        {summary.errorMessage && <p role="alert" style={{ color: '#B91C1C', fontSize: 13, margin: '12px 0 0' }}>{summary.errorMessage}</p>}

        {summary.pageCount > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
              <span>{active ? 'Reading pages — drafts appear below as each page finishes.' : 'Pages read'}</span>
              <span>{summary.pagesProcessed} / {summary.pageCount}</span>
            </div>
            <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Pages read"
                 style={{ height: 8, background: '#F3F4F6', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-primary,#F29213)', transition: 'width .4s' }} />
            </div>
          </div>
        )}
        {summary.status === 'UPLOADED' && <p style={{ ...muted, marginTop: 12 }}>Preparing pages…</p>}

        <div style={{ marginTop: 16 }}>
          <PageStrip pages={detail.pages} running={summary.running} loadPages={loadPages} onReread={handleReread} />
        </div>
      </section>

      {summary.status === 'AWAITING_PAGES' ? (
        <PagePicker pages={detail.pages} onSubmit={handleSelectPages} busy={startingPages} autoStartAt={summary.autoStartAt} />
      ) : (<>
      <div role="tablist" aria-label="Draft status" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t.key || 'all'} role="tab" aria-selected={tab === t.key}
                  onClick={() => { setTab(t.key); setPage(0); }}
                  style={{ ...btnGhost, ...(tab === t.key ? { borderColor: '#F29213', color: '#B15400', background: '#FEF3E2' } : {}) }}>
            {t.text}{t.key && counts[t.key] !== undefined ? ` (${counts[t.key]})` : ''}
          </button>
        ))}
      </div>

      {failures.length > 0 && (
        <div role="alert" style={{ ...card, borderColor: '#FCA5A5', background: '#FEF2F2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: 14, color: '#991B1B' }}>Not created</strong>
            <button style={{ ...btnGhost, padding: '2px 10px' }} onClick={() => setFailures([])} aria-label="Dismiss">×</button>
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: '#7F1D1D' }}>
            {failures.map((f) => <li key={f.itemId}><strong>{f.name}:</strong> {f.error}</li>)}
          </ul>
        </div>
      )}

      {pendingOnPage.length > 0 && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 18px', position: 'sticky', top: 8, zIndex: 5 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width: 18, height: 18, accentColor: '#F29213' }} />
            Select all on this page
          </label>
          <span style={muted}>{selected.size} selected</span>
          <button style={{ ...btnPrimary, marginLeft: 'auto', opacity: selected.size === 0 || creating ? 0.6 : 1 }}
                  disabled={selected.size === 0 || creating} onClick={createSelected}>
            {creating ? 'Creating…' : `Create ${selected.size || ''} product${selected.size === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {itemsLoading && items.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>Loading drafts…</p>
      ) : items.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>
          {active ? 'No drafts yet — they appear here as pages are read.'
            : tab === 'PENDING_REVIEW' ? 'Nothing left to review.' : 'No drafts here.'}
        </p>
      ) : (
        items.map((item) => (
          <DraftCard
            key={item.id}
            item={item}
            campaign={summary.campaign}
            onReload={loadItems}
            loadPages={loadPages}
            brands={brands}
            categories={categories}
            selected={selected.has(item.id)}
            onToggleSelect={toggleSelect}
            onItemChanged={onItemChanged}
            onCreate={(draft) => runCreate([draft])}
            onDirtyChange={onDirtyChange}
          />
        ))
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
      </>)}
    </div>
  );
}
